import { Link } from 'react-router-dom';
import './Legal.css';

const LAST_UPDATED = '1 March 2025';
const COMPANY = 'SBH The Company';
const EMAIL = 'legal@transportily.app';

export default function Terms() {
  return (
    <div className="page legal-page">
      <div className="container legal-container">
        <div className="legal-header">
          <Link to="/" className="auth-logo" style={{ marginBottom: '24px', display: 'inline-block' }}>
            Transport<span>ily</span>
          </Link>
          <h1>Terms of Service</h1>
          <p className="legal-meta">Last updated: {LAST_UPDATED} · Governed by German law</p>
        </div>

        <div className="legal-body">

          <section>
            <h2>1. Scope and operator</h2>
            <p>
              These Terms of Service ("Terms") govern the use of the Transportily platform, available at transportily.app and its subdomains (the "Platform"), operated by <strong>{COMPANY}</strong> ("Transportily", "we", "us").
            </p>
            <p>
              By registering an account or using the Platform you agree to these Terms in full. If you do not agree, do not use the Platform.
            </p>
          </section>

          <section>
            <h2>2. Nature of the platform</h2>
            <p>
              Transportily is an <strong>intermediary marketplace</strong>. We connect private individuals ("Transporters") travelling between Germany and Tunisia who have spare luggage capacity, with other private individuals ("Senders") who wish to send personal goods or documents on those journeys.
            </p>
            <p>
              <strong>Transportily is not a transport company, freight forwarder, or carrier.</strong> We do not take physical possession of any goods at any time. We do not arrange, supervise, or guarantee any transport. All transport is carried out by independent private Transporters acting in their own capacity.
            </p>
            <p>
              The contractual relationship for the transport of goods is formed exclusively between the Sender and the Transporter. Transportily is not a party to that contract.
            </p>
          </section>

          <section>
            <h2>3. Eligibility</h2>
            <p>You may use the Platform only if you:</p>
            <ul>
              <li>are at least 18 years old;</li>
              <li>have full legal capacity under applicable law;</li>
              <li>are acting as a private individual, not as a commercial freight broker or carrier;</li>
              <li>provide accurate, complete, and truthful information during registration and use.</li>
            </ul>
          </section>

          <section>
            <h2>4. User accounts</h2>
            <p>
              You are responsible for maintaining the confidentiality of your account credentials. You are liable for all activity that occurs under your account. You must notify us immediately at <a href={`mailto:${EMAIL}`}>{EMAIL}</a> if you suspect unauthorised access.
            </p>
            <p>
              We reserve the right to suspend or permanently close accounts that violate these Terms, provide false information, or engage in fraudulent, abusive, or illegal conduct.
            </p>
          </section>

          <section>
            <h2>5. Transporter obligations</h2>
            <p>By posting a trip and accepting bookings as a Transporter, you represent and warrant that:</p>
            <ul>
              <li>You are making the journey described and have genuine spare luggage capacity.</li>
              <li>You will only carry goods that are <strong>legal to transport</strong> across the relevant borders under German, Tunisian, and EU customs law.</li>
              <li>You will not carry prohibited or restricted items including (but not limited to): weapons, drugs, counterfeit goods, endangered species products, currency above customs thresholds, undeclared commercial goods, or any item whose import or export is restricted.</li>
              <li>You will declare all goods to customs authorities as required by law and cooperate fully with border control.</li>
              <li>You accept full personal legal responsibility for your compliance with customs and border regulations.</li>
              <li>You will deliver the Sender's goods with reasonable care and within the agreed timeframe.</li>
            </ul>
            <p>
              <strong>Transportily explicitly disclaims all liability</strong> for any customs seizure, fine, criminal investigation, or legal consequence arising from a Transporter's failure to comply with applicable law.
            </p>
          </section>

          <section>
            <h2>6. Sender obligations</h2>
            <p>By placing a booking as a Sender, you represent and warrant that:</p>
            <ul>
              <li>All items you submit for transport are <strong>lawful to export from Germany</strong> and <strong>lawful to import into Tunisia</strong> (or vice versa), and comply with all applicable customs and tax regulations.</li>
              <li>You provide accurate descriptions of all items, their nature, quantity, and declared value.</li>
              <li>You do not misrepresent the contents of packages or conceal restricted items.</li>
              <li>You understand that providing false or incomplete declarations may constitute customs fraud under <em>§ 370 Abgabenordnung</em> (AO) and other applicable laws.</li>
              <li>You are solely responsible for any customs duties, taxes, or fines arising from your shipment.</li>
            </ul>
            <p>
              <strong>Transportily is not responsible</strong> for any legal consequence, seizure, or financial loss arising from unlawful or misdeclared goods.
            </p>
          </section>

          <section>
            <h2>7. Platform fees and payments</h2>
            <p>
              Transportily charges a <strong>platform fee of 10%</strong> of the agreed transport price, paid by the Sender at the time of booking. This fee covers platform operation, payment processing, and basic dispute facilitation.
            </p>
            <p>
              Online payments are processed via Stripe. Cash payments are made directly between Sender and Transporter; only the platform fee is collected online in such cases.
            </p>
            <p>
              The platform fee is <strong>non-refundable</strong> except where expressly stated in our Refund Policy. If a Transporter cancels a confirmed booking, the Sender receives a full refund including the platform fee.
            </p>
            <p>
              Transporters may request withdrawal of their wallet balance subject to our payout terms. We reserve the right to withhold payouts pending resolution of disputes or fraud investigations.
            </p>
          </section>

          <section>
            <h2>8. Cancellations and refunds</h2>
            <ul>
              <li><strong>Transporter cancels:</strong> Sender receives a full refund of all amounts paid.</li>
              <li><strong>Sender cancels before confirmation:</strong> Full refund.</li>
              <li><strong>Sender cancels after confirmation:</strong> Platform fee is non-refundable. The transport fee portion may be refunded at Transportily's discretion.</li>
              <li><strong>Goods not delivered / lost:</strong> Transporters are personally liable to the Sender under civil law. Transportily will cooperate with reasonable dispute resolution but does not guarantee recovery of lost goods.</li>
            </ul>
          </section>

          <section>
            <h2>9. Limitation of liability</h2>
            <p>
              To the maximum extent permitted by applicable law, <strong>Transportily's total liability</strong> to any user arising from or related to use of the Platform shall not exceed <strong>the platform fee paid by that user in the 6 months preceding the claim</strong>.
            </p>
            <p>
              Transportily is not liable for:
            </p>
            <ul>
              <li>Loss, theft, damage, or seizure of goods in transit;</li>
              <li>Customs duties, fines, or legal sanctions;</li>
              <li>Delays, cancellations, or no-shows by Transporters;</li>
              <li>Indirect, consequential, or special damages of any kind;</li>
              <li>Acts or omissions of users of the Platform.</li>
            </ul>
            <p>
              Nothing in these Terms limits liability for death or personal injury caused by negligence, or for fraud or fraudulent misrepresentation, as required under German law (<em>§ 309 Nr. 7 BGB</em>).
            </p>
          </section>

          <section>
            <h2>10. Prohibited conduct</h2>
            <p>You must not use the Platform to:</p>
            <ul>
              <li>Transport or facilitate the transport of illegal goods;</li>
              <li>Evade customs duties or taxes;</li>
              <li>Engage in money laundering or fraud;</li>
              <li>Harass, threaten, or deceive other users;</li>
              <li>Circumvent platform fees by conducting transactions off-platform after initial contact via Transportily;</li>
              <li>Post false trip listings or fake reviews;</li>
              <li>Use automated tools to scrape or abuse the Platform.</li>
            </ul>
          </section>

          <section>
            <h2>11. Intellectual property</h2>
            <p>
              All content, trademarks, and software on the Platform are owned by or licensed to Transportily. You may not copy, reproduce, or create derivative works without our express written consent.
            </p>
            <p>
              You grant Transportily a non-exclusive, royalty-free licence to display the content you submit (reviews, profile information, trip listings) for the purposes of operating the Platform.
            </p>
          </section>

          <section>
            <h2>12. Termination</h2>
            <p>
              You may close your account at any time by contacting us. We may suspend or terminate your account immediately if you violate these Terms, without prejudice to any claims we may have against you.
            </p>
            <p>
              On termination, your data will be handled in accordance with our <Link to="/privacy">Privacy Policy</Link>.
            </p>
          </section>

          <section>
            <h2>13. Governing law and jurisdiction</h2>
            <p>
              These Terms are governed by and construed in accordance with the laws of the <strong>Federal Republic of Germany</strong>, excluding its conflict of law provisions.
            </p>
            <p>
              Any dispute arising from these Terms shall be subject to the exclusive jurisdiction of the courts of <strong>Berlin, Germany</strong>, unless mandatory consumer protection law in your country of residence requires otherwise.
            </p>
            <p>
              EU online dispute resolution platform: <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noreferrer">https://ec.europa.eu/consumers/odr</a>. We are not obligated to participate in alternative dispute resolution proceedings but will consider them in good faith.
            </p>
          </section>

          <section>
            <h2>14. Changes to these Terms</h2>
            <p>
              We may update these Terms at any time. Material changes will be communicated via email or in-app notification at least 30 days before they take effect. Continued use of the Platform after changes take effect constitutes acceptance of the revised Terms.
            </p>
          </section>

          <section>
            <h2>15. Contact</h2>
            <p>
              {COMPANY}<br />
              Email: <a href={`mailto:${EMAIL}`}>{EMAIL}</a>
            </p>
          </section>

        </div>

        <div className="legal-footer">
          <Link to="/privacy">Privacy Policy</Link>
          <span>·</span>
          <Link to="/">Back to Transportily</Link>
        </div>
      </div>
    </div>
  );
}